import React, { createContext, useContext, ReactNode } from "react";
import { EntityRecord } from "@/types/entity";
import { Location } from "@/bryntum-lib/models/Location";

// ─── Types ────────────────────────────────────────────────────────────────────

interface EntityLocationContextValue {
  locationRecord: Location | null;
  hasLocation: boolean;
  record: EntityRecord;
  initialAddress?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isNonEmpty(val: unknown): boolean {
  if (typeof val === "string") return val.trim().length > 0;
  if (typeof val === "number") return val !== null && val !== undefined;
  return false;
}

function resolveLocation(record: EntityRecord): Location | null {
  return (record as unknown as { location: Location | null }).location ?? null;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const EntityLocationContext = createContext<EntityLocationContextValue | undefined>(undefined);

interface EntityLocationProviderProps {
  children: ReactNode;
  record: EntityRecord;
  initialAddress?: string;
}

export const EntityLocationProvider: React.FC<EntityLocationProviderProps> = ({ children, record, initialAddress }) => {
  const locationRecord = resolveLocation(record);
  const hasLocation =
    !!locationRecord && (isNonEmpty(locationRecord.streetAddress) || isNonEmpty(locationRecord.latitude));

  return (
    <EntityLocationContext.Provider value={{ locationRecord, hasLocation, record, initialAddress }}>
      {children}
    </EntityLocationContext.Provider>
  );
};

export const useEntityLocationContext = (): EntityLocationContextValue => {
  const context = useContext(EntityLocationContext);
  if (!context) {
    throw new Error("useEntityLocationContext must be used within EntityLocationProvider");
  }
  return context;
};
